import {
  CHART_LIMITS,
  HEX_COLOUR_RE,
  REQUIRED_PALETTE_KEYS,
  type BaseChart,
  type ChartValidationIssue,
  type ChartValidationResult,
} from './chart.js';
import { VARIANTS, type Variant } from './gameTypes.js';

/**
 * Waves uses its OWN taller playfield than the shared
 * {@link import('./gameTypes.js').PLAYFIELD_HEIGHT} (540, which Pulse
 * depends on). A winding terrain corridor needs vertical headroom so
 * the ramps and walls read clearly without pinching the playable band
 * — so the waves engine, chart validation, geometry, and renderer all
 * speak in this coordinate space instead.
 *
 * Width stays {@link import('./gameTypes.js').PLAYFIELD_WIDTH} (960),
 * so the playfield is 4:3. When the viewport aspect differs, gameplay
 * (player + obstacles) is letterboxed to the centred playfield, but the
 * corridor terrain renders edge to edge into the letterbox margins so
 * the map fills the viewport with no dark gaps.
 */
export const WAVES_PLAYFIELD_HEIGHT = 720;

/** Top wall of the waves playfield (absolute upper bound for terrain). */
export const WAVES_CEILING_Y = 60;

/** Bottom wall of the waves playfield (absolute lower bound for terrain). */
export const WAVES_FLOOR_Y = 660;

/**
 * Waves-mode chart payload. The mode is a spacewaves.io / Geometry
 * Dash–wave style one-button rhythm runner: the player auto-scrolls
 * right, and holding the input flips them onto an ascending diagonal,
 * releasing flips them onto a descending diagonal. Failure is one-shot
 * — touching any obstacle ends the run immediately. Stars / cups gate
 * on the same loop-progress milestones as every other mode.
 *
 * Obstacles are derived deterministically at module-load time from the
 * matching `(slug, variant)` row of the Circles osu-imported JSON
 * already shipped for Circles + Guitar mode. See
 * `builtinWavesMaps/wavesFromCircles.ts` for the mapping rules.
 */
export interface WavesChart extends BaseChart {
  mode: 'waves';
  /**
   * Vertical position of the player at beat 0 (in playfield units).
   * Authors pick a spawn that doesn't immediately collide with the
   * first obstacle. The renderer interpolates from this Y on session
   * start so the player enters cleanly even on charts whose first
   * obstacle is right on beat 1.
   */
  spawnY: number;
  /**
   * Vertical speed (playfield units per beat) the player moves while
   * the input is held (`-vY` direction = toward WAVES_CEILING_Y) or
   * released (`+vY` direction = toward WAVES_FLOOR_Y). Per-variant via
   * {@link WAVES_VARIANT_TUNING.vYPerBeat}.
   */
  playerVyPerBeat: number;
  /**
   * Hit-test radius of the player's bounding circle. Smaller = harder
   * to thread tight gaps but more forgiving on grazing approaches.
   */
  playerRadius: number;
  /**
   * Ordered list of obstacles the player must navigate past. Sorted by
   * `beat` ascending. Limit: {@link MAX_WAVES_OBSTACLES}.
   */
  obstacles: WavesObstacle[];
  /**
   * Winding-corridor terrain (floor + ceiling polylines). Optional:
   * legacy charts without terrain fall back to a flat full-corridor
   * via {@link resolveWavesTerrain}, preserving pre-terrain behaviour.
   */
  terrain?: WavesTerrain;
}

export type WavesObstacle = WavesGate | WavesSaw;

/**
 * A vertical wall the player must thread through. The "gap" between
 * {@link gapTopY} and {@link gapBottomY} is the safe corridor at the
 * obstacle's beat — anything outside that band kills on impact.
 *
 * Gates double as spikes and tunnel-narrowings (built in
 * `makeSpikeGate` / `makeTunnelGate`):
 *   - Floor spike: safe band hugs the ceiling (`gapTopY = ceiling`,
 *     `gapBottomY = ceiling + minGap`); a lethal pillar rises from the
 *     floor, forcing the ship to climb.
 *   - Ceiling spike: safe band hugs the floor (`gapTopY = floor − minGap`,
 *     `gapBottomY = floor`); a lethal pillar hangs from the ceiling,
 *     forcing the ship to dive.
 *   - Tunnel pinch (slider): smaller gap centred mid-corridor.
 *
 * Mode-specific renderer reads {@link visual} to pick the artwork
 * (triangle, vertical bar, etc.); the engine ignores it.
 */
export interface WavesGate {
  id: string;
  kind: 'gate';
  beat: number;
  gapTopY: number;
  gapBottomY: number;
  visual?: WavesGateVisual;
}

export type WavesGateVisual = 'spike-floor' | 'spike-ceiling' | 'pillar' | 'gap';

/**
 * Circular hazard (spinning saw blade aesthetic). Kills if the player's
 * bounding circle overlaps the saw's body at the saw's beat.
 *
 * Saws are reforged from single-sided spikes (`injectSaws` in
 * `wavesFromCircles.ts`): mounted flush against one corridor surface, so
 * the player pins to the OPEN side and arcs their circular body around the
 * blade rather than threading a flat gap. `centerY` bakes a beat-keyed
 * oscillation so it stays replay-deterministic.
 */
export interface WavesSaw {
  id: string;
  kind: 'saw';
  beat: number;
  centerY: number;
  radius: number;
}

/**
 * One vertex of a terrain surface (the corridor floor or ceiling).
 * Consecutive vertices form segments:
 *   - **ramp**: `beat` strictly increases; the surface interpolates
 *     linearly and the ship GLIDES along it (non-lethal). Ramp slope
 *     magnitude is capped at `playerVyPerBeat`, so the ship can never
 *     separate from a surface faster than it moves — this is what keeps
 *     the glide-clamp resolution-independent (client per-frame vs
 *     server 1/16-beat) and therefore replay-deterministic.
 *   - **wall**: two consecutive vertices share a `beat` (a vertical
 *     face). A floor step UP (later `y` smaller) and a ceiling step
 *     DOWN (later `y` larger) are LETHAL — the ship must clear the face
 *     before reaching it or it dies on impact (a 90-degree wall, unlike
 *     a glide-able ramp).
 */
export interface WavesTerrainNode {
  beat: number;
  y: number;
}

/**
 * Winding-corridor terrain. `floor` and `ceiling` are piecewise-linear
 * polylines over `[0, totalBeats]`, sorted by `beat` (equal beats allowed
 * for vertical walls). The ship must stay inside the gap; ramps are
 * glide surfaces, vertical walls are lethal.
 */
export interface WavesTerrain {
  floor: WavesTerrainNode[];
  ceiling: WavesTerrainNode[];
}

/** A lethal vertical face, normalised so `top < bottom` in playfield Y. */
export interface WavesTerrainWall {
  beat: number;
  /** Smaller Y (higher on screen). */
  top: number;
  /** Larger Y (lower on screen). */
  bottom: number;
  /**
   * Which surface the wall belongs to. A `floor` wall (upward step) is
   * cleared by staying ABOVE `top`; a `ceiling` wall (downward step) is
   * cleared by staying BELOW `bottom`. The lethal hit-test is identical
   * for both; only the planner's clear-band side differs.
   */
  surface: 'floor' | 'ceiling';
}

/**
 * Minimum playable corridor height (floor − ceiling) the terrain must
 * preserve everywhere, on top of the player's diameter. Keeps the
 * glide-clamp interval non-empty and leaves the ship room to maneuver
 * between walls.
 */
export const WAVES_MIN_CORRIDOR_MARGIN = 44;

/** Flat full-corridor terrain spanning `[0, totalBeats]`. */
export function flatWavesTerrain(totalBeats: number): WavesTerrain {
  const end = Math.max(totalBeats, 1);
  return {
    floor: [
      { beat: 0, y: WAVES_FLOOR_Y },
      { beat: end, y: WAVES_FLOOR_Y },
    ],
    ceiling: [
      { beat: 0, y: WAVES_CEILING_Y },
      { beat: end, y: WAVES_CEILING_Y },
    ],
  };
}

/** Resolve a chart's terrain, defaulting to a flat full corridor. */
export function resolveWavesTerrain(chart: WavesChart): WavesTerrain {
  return chart.terrain ?? flatWavesTerrain(chart.totalBeats);
}

/**
 * Surface height at `beat`, left-continuous at walls: at a wall's exact
 * beat it returns the PRE-wall surface, so a gliding ship reads as still
 * on the lower floor and the wall's lethal check (not the clamp) decides
 * the impact. Binary-searched, O(log n).
 */
export function surfaceYAt(nodes: ReadonlyArray<WavesTerrainNode>, beat: number): number {
  const n = nodes.length;
  if (n === 0) {
    return 0;
  }
  if (beat <= nodes[0].beat) {
    return nodes[0].y;
  }
  if (beat >= nodes[n - 1].beat) {
    return nodes[n - 1].y;
  }
  // lower_bound: first index whose beat is >= `beat`. With duplicate
  // beats (walls) this lands on the FIRST (pre-wall) node, giving
  // left-continuity at the wall.
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (nodes[mid].beat < beat) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  if (nodes[lo].beat === beat) {
    return nodes[lo].y;
  }
  const a = nodes[lo - 1];
  const c = nodes[lo];
  return a.y + ((c.y - a.y) * (beat - a.beat)) / (c.beat - a.beat);
}

export function floorYAt(terrain: WavesTerrain, beat: number): number {
  return surfaceYAt(terrain.floor, beat);
}

export function ceilingYAt(terrain: WavesTerrain, beat: number): number {
  return surfaceYAt(terrain.ceiling, beat);
}

/** Lethal upward floor steps (the floor face the ship can crash into). */
export function terrainFloorWalls(terrain: WavesTerrain): WavesTerrainWall[] {
  const walls: WavesTerrainWall[] = [];
  const floor = terrain.floor;
  for (let i = 0; i + 1 < floor.length; i += 1) {
    const a = floor[i];
    const c = floor[i + 1];
    if (a.beat === c.beat && c.y < a.y) {
      walls.push({ beat: a.beat, top: c.y, bottom: a.y, surface: 'floor' });
    }
  }
  return walls;
}

/** Lethal downward ceiling steps. */
export function terrainCeilingWalls(terrain: WavesTerrain): WavesTerrainWall[] {
  const walls: WavesTerrainWall[] = [];
  const ceiling = terrain.ceiling;
  for (let i = 0; i + 1 < ceiling.length; i += 1) {
    const a = ceiling[i];
    const c = ceiling[i + 1];
    if (a.beat === c.beat && c.y > a.y) {
      walls.push({ beat: a.beat, top: a.y, bottom: c.y, surface: 'ceiling' });
    }
  }
  return walls;
}

/** All lethal walls (floor + ceiling), sorted by beat ascending. */
export function terrainWalls(terrain: WavesTerrain): WavesTerrainWall[] {
  return [...terrainFloorWalls(terrain), ...terrainCeilingWalls(terrain)].sort(
    (x, y) => x.beat - y.beat,
  );
}

/**
 * Per-variant gameplay tuning for Waves mode.
 *
 * Three knobs vary with variant:
 *   - `vYPerBeat`: how steeply the player climbs/descends per beat
 *     (visual slope = arctan(vYPerBeat / SCROLL_SPEED_PX_PER_BEAT)).
 *   - `playerRadius`: hit-test bubble around the player. Smaller =
 *     harder to thread tight gaps.
 *   - `minGapHeight`: floor for how tight a derived gap is allowed to
 *     be. Tighter on harder variants — combined with sharper slope,
 *     this is what makes hard / extreme charts actually demand
 *     precise inputs.
 *
 * Tuned so Normal feels like a casual side-scroller, Hard demands
 * recognisable osu-style reading + reactions, and Extreme approaches
 * Geometry-Dash–wave levels of input rigour without becoming
 * unreadable at the chart's BPM.
 */
export const WAVES_VARIANT_TUNING: Record<
  Variant,
  { vYPerBeat: number; playerRadius: number; minGapHeight: number }
> = {
  normal: { vYPerBeat: 130, playerRadius: 14, minGapHeight: 260 },
  hard: { vYPerBeat: 180, playerRadius: 12, minGapHeight: 140 },
  extreme: { vYPerBeat: 230, playerRadius: 10, minGapHeight: 95 },
};

/**
 * Score awarded per obstacle the player passes alive with a perfect
 * line (centred in the gap). Mirrors Pulse's {@link PERFECT_SCORE};
 * good passes use {@link GOOD_SCORE} from gameTypes. Combo multiplier
 * scales from there.
 */
export const WAVES_OBSTACLE_PASS_SCORE = 300;

/**
 * Horizontal scroll speed multiplier — obstacles cover the playfield
 * width in fewer beats, so the run feels snappier without changing
 * vertical climb/dive speed. Affects visuals only; chart timing is
 * always anchored to the audio's beat clock.
 */
export const WAVES_HORIZONTAL_SPEED_MUL = 1.75;

/**
 * Inner fraction of the playable gap height (after player radius) that
 * counts as a perfect pass. The remainder of the gap awards good.
 */
export const WAVES_PERFECT_CENTER_BAND_FRAC = 0.4;

/**
 * Maximum span of obstacle list. Generous to support kiai-dense extreme
 * charts (Nekozilla extreme can clear 400 hit objects in 60s); below
 * `maxCircleHitObjects` from circles to keep the seeder's blob size
 * comparable.
 */
export const MAX_WAVES_OBSTACLES = 1500;

/**
 * Spawn position for charts that don't specify one. Centred between the
 * floor and ceiling so the very first hold/release reaches either
 * spike in the same number of beats.
 */
export const WAVES_DEFAULT_SPAWN_Y = (WAVES_CEILING_Y + WAVES_FLOOR_Y) / 2;

/**
 * Find the gate gap at a specific beat. Returns null if no gate sits
 * at that exact beat. Wave gates are point-events (not spans), so
 * "active" only means "at this beat".
 */
export function findGateAtBeat(
  obstacles: ReadonlyArray<WavesObstacle>,
  beat: number,
  epsilon = 1e-6,
): WavesGate | null {
  for (const obs of obstacles) {
    if (obs.kind === 'gate' && Math.abs(obs.beat - beat) < epsilon) {
      return obs;
    }
  }
  return null;
}

export function validateWavesChart(chart: WavesChart): ChartValidationResult {
  const issues: ChartValidationIssue[] = [];

  validateBaseFields(chart, issues);

  if (!Number.isFinite(chart.spawnY) || chart.spawnY < 0 || chart.spawnY > WAVES_PLAYFIELD_HEIGHT) {
    issues.push({
      level: 'error',
      message: `spawnY ${chart.spawnY} must be in [0, ${WAVES_PLAYFIELD_HEIGHT}]`,
    });
  }
  if (
    !Number.isFinite(chart.playerVyPerBeat) ||
    chart.playerVyPerBeat <= 0 ||
    chart.playerVyPerBeat > 1000
  ) {
    issues.push({
      level: 'error',
      message: `playerVyPerBeat ${chart.playerVyPerBeat} must be in (0, 1000]`,
    });
  }
  if (
    !Number.isFinite(chart.playerRadius) ||
    chart.playerRadius < 4 ||
    chart.playerRadius > 60
  ) {
    issues.push({
      level: 'error',
      message: `playerRadius ${chart.playerRadius} out of range (4-60 playfield units)`,
    });
  }

  if (!Array.isArray(chart.obstacles) || chart.obstacles.length === 0) {
    issues.push({ level: 'error', message: 'Waves chart needs at least one obstacle' });
  } else if (chart.obstacles.length > MAX_WAVES_OBSTACLES) {
    issues.push({
      level: 'error',
      message: `obstacles ${chart.obstacles.length} exceeds limit ${MAX_WAVES_OBSTACLES}`,
    });
  }

  validateObstacles(chart, issues);
  validateTerrain(chart, issues);

  return {
    ok: !issues.some((i) => i.level === 'error'),
    issues,
  };
}

/**
 * Validate the optional terrain polylines. Enforces the invariants the
 * deterministic engine relies on: sorted nodes spanning the chart,
 * ramp slopes within `playerVyPerBeat` (so the glide-clamp stays
 * resolution-independent), a corridor that never pinches below the
 * playable minimum, and a spawn that starts inside the corridor.
 */
function validateTerrain(chart: WavesChart, issues: ChartValidationIssue[]): void {
  const terrain = chart.terrain;
  if (terrain === undefined) {
    return;
  }
  const r = chart.playerRadius;
  const vY = chart.playerVyPerBeat;
  const surfaces: ReadonlyArray<readonly [string, WavesTerrainNode[]]> = [
    ['floor', terrain.floor],
    ['ceiling', terrain.ceiling],
  ];
  for (const [name, nodes] of surfaces) {
    if (!Array.isArray(nodes) || nodes.length < 2) {
      issues.push({ level: 'error', message: `terrain.${name} needs at least 2 nodes` });
      continue;
    }
    if (nodes[0].beat !== 0) {
      issues.push({ level: 'error', message: `terrain.${name} must start at beat 0` });
    }
    if (nodes[nodes.length - 1].beat < chart.totalBeats - 1e-6) {
      issues.push({
        level: 'error',
        message: `terrain.${name} must extend to totalBeats (${chart.totalBeats})`,
      });
    }
    let prevBeat = -Infinity;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (!Number.isFinite(node.beat) || node.beat < 0 || node.beat > chart.totalBeats + 1e-6) {
        issues.push({
          level: 'error',
          message: `terrain.${name} node ${i} beat ${node.beat} outside [0, ${chart.totalBeats}]`,
          beat: node.beat,
        });
      }
      if (node.beat < prevBeat - 1e-9) {
        issues.push({
          level: 'error',
          message: `terrain.${name} node ${i} beat ${node.beat} is out of order`,
        });
      }
      prevBeat = node.beat;
      if (!Number.isFinite(node.y) || node.y < 0 || node.y > WAVES_PLAYFIELD_HEIGHT) {
        issues.push({
          level: 'error',
          message: `terrain.${name} node ${i} y ${node.y} outside [0, ${WAVES_PLAYFIELD_HEIGHT}]`,
        });
      }
      if (i + 1 < nodes.length) {
        const next = nodes[i + 1];
        const db = next.beat - node.beat;
        if (db > 1e-9) {
          const slope = Math.abs(next.y - node.y) / db;
          if (slope > vY + 1e-6) {
            issues.push({
              level: 'error',
              message: `terrain.${name} segment ${i} slope ${slope.toFixed(1)} exceeds playerVyPerBeat ${vY} (ramps must be glide-able)`,
              beat: node.beat,
            });
          }
        }
      }
    }
  }

  // Corridor width: width(beat) is piecewise-linear with jumps at walls,
  // so its minimum lands on a node beat. Checking each node's own y
  // against the opposite surface at that beat covers both sides of any
  // wall jump (the wall's top/bottom nodes are both present).
  const minWidth = 2 * r + WAVES_MIN_CORRIDOR_MARGIN;
  for (const node of terrain.floor) {
    const width = node.y - surfaceYAt(terrain.ceiling, node.beat);
    if (width < minWidth - 1e-6) {
      issues.push({
        level: 'error',
        message: `terrain corridor width ${width.toFixed(1)} at beat ${node.beat} is below ${minWidth}`,
        beat: node.beat,
      });
    }
  }
  for (const node of terrain.ceiling) {
    const width = surfaceYAt(terrain.floor, node.beat) - node.y;
    if (width < minWidth - 1e-6) {
      issues.push({
        level: 'error',
        message: `terrain corridor width ${width.toFixed(1)} at beat ${node.beat} is below ${minWidth}`,
        beat: node.beat,
      });
    }
  }

  // Spawn must start inside the corridor at beat 0.
  const spawnLo = surfaceYAt(terrain.ceiling, 0) + r;
  const spawnHi = surfaceYAt(terrain.floor, 0) - r;
  if (chart.spawnY < spawnLo - 1e-6 || chart.spawnY > spawnHi + 1e-6) {
    issues.push({
      level: 'error',
      message: `spawnY ${chart.spawnY} not inside terrain corridor [${spawnLo}, ${spawnHi}] at beat 0`,
    });
  }
}

function validateBaseFields(chart: WavesChart, issues: ChartValidationIssue[]): void {
  if (!chart.id) {
    issues.push({ level: 'error', message: 'Chart id is required' });
  }
  const title = chart.title ?? '';
  if (!title.trim()) {
    issues.push({ level: 'error', message: 'Chart title is required' });
  }
  if (title.length > CHART_LIMITS.maxTitleLength) {
    issues.push({
      level: 'error',
      message: `Title exceeds ${CHART_LIMITS.maxTitleLength} chars`,
    });
  }
  if ((chart.description ?? '').length > CHART_LIMITS.maxDescriptionLength) {
    issues.push({
      level: 'error',
      message: `Description exceeds ${CHART_LIMITS.maxDescriptionLength} chars`,
    });
  }
  if (chart.mapper !== undefined) {
    if (typeof chart.mapper !== 'string' || chart.mapper.trim().length === 0) {
      issues.push({
        level: 'error',
        message: 'mapper, when present, must be a non-empty string',
      });
    } else if (chart.mapper.length > CHART_LIMITS.maxMapperLength) {
      issues.push({
        level: 'error',
        message: `Mapper exceeds ${CHART_LIMITS.maxMapperLength} chars`,
      });
    }
  }
  if (!Number.isFinite(chart.bpm) || chart.bpm < 40 || chart.bpm > 500) {
    issues.push({
      level: 'error',
      message: `BPM ${chart.bpm} out of range (40-500)`,
    });
  }
  if (!Number.isFinite(chart.totalBeats) || chart.totalBeats <= 0) {
    issues.push({ level: 'error', message: 'totalBeats must be > 0' });
  }
  if (Number.isFinite(chart.totalBeats) && chart.totalBeats > CHART_LIMITS.maxTotalBeats) {
    issues.push({
      level: 'error',
      message: `totalBeats ${chart.totalBeats} exceeds limit ${CHART_LIMITS.maxTotalBeats}`,
    });
  }
  if (
    !Number.isInteger(chart.difficulty) ||
    chart.difficulty < 1 ||
    chart.difficulty > 5
  ) {
    issues.push({
      level: 'error',
      message: `difficulty ${chart.difficulty} must be integer 1-5`,
    });
  }
  if (!(VARIANTS as ReadonlyArray<string>).includes(chart.variant)) {
    issues.push({
      level: 'error',
      message: `variant ${chart.variant} must be one of ${VARIANTS.join(', ')}`,
    });
  }

  if (!chart.audio || typeof chart.audio.src !== 'string' || chart.audio.src.length === 0) {
    issues.push({ level: 'error', message: 'audio.src is required' });
  } else {
    if (!Number.isFinite(chart.audio.bpm) || chart.audio.bpm !== chart.bpm) {
      issues.push({
        level: 'error',
        message: `audio.bpm (${chart.audio.bpm}) must match chart.bpm (${chart.bpm})`,
      });
    }
    if (!Number.isFinite(chart.audio.offsetMs) || chart.audio.offsetMs < 0) {
      issues.push({ level: 'error', message: 'audio.offsetMs must be a non-negative number' });
    }
    if (!Number.isFinite(chart.audio.durationSec) || chart.audio.durationSec <= 0) {
      issues.push({ level: 'error', message: 'audio.durationSec must be > 0' });
    }
    if ((chart.audio.onsets?.length ?? 0) > CHART_LIMITS.maxOnsets) {
      issues.push({
        level: 'error',
        message: `audio.onsets ${chart.audio.onsets.length} exceeds limit ${CHART_LIMITS.maxOnsets}`,
      });
    }
  }

  for (const key of REQUIRED_PALETTE_KEYS) {
    const value = chart.palette?.[key];
    if (typeof value !== 'string' || !HEX_COLOUR_RE.test(value)) {
      issues.push({
        level: 'error',
        message: `palette.${key} must be a hex colour like #RRGGBB`,
      });
    }
  }
}

function validateObstacles(chart: WavesChart, issues: ChartValidationIssue[]): void {
  if (!Array.isArray(chart.obstacles)) {
    return;
  }
  const ids = new Set<string>();
  const sorted = [...chart.obstacles].sort((a, b) => a.beat - b.beat);
  let prevBeat = -Infinity;

  for (const obs of sorted) {
    if (!obs.id) {
      issues.push({ level: 'error', message: 'Obstacle missing id', beat: obs.beat });
      continue;
    }
    if (ids.has(obs.id)) {
      issues.push({ level: 'error', message: `Duplicate obstacle id ${obs.id}` });
    }
    ids.add(obs.id);

    if (!Number.isFinite(obs.beat) || obs.beat < 0 || obs.beat > chart.totalBeats) {
      issues.push({
        level: 'error',
        message: `Obstacle ${obs.id} at beat ${obs.beat} outside [0, ${chart.totalBeats}]`,
        beat: obs.beat,
      });
      continue;
    }

    if (obs.beat < prevBeat) {
      // Sort guarantees this never fires once the sort is honoured;
      // explicit check catches future authoring shortcuts that bypass
      // the sort step.
      issues.push({
        level: 'error',
        message: `Obstacle ${obs.id} at beat ${obs.beat} is out of order (must be ascending after sort)`,
      });
    }
    prevBeat = obs.beat;

    if (obs.kind === 'gate') {
      if (
        !Number.isFinite(obs.gapTopY) ||
        !Number.isFinite(obs.gapBottomY) ||
        obs.gapTopY < 0 ||
        obs.gapBottomY > WAVES_PLAYFIELD_HEIGHT ||
        obs.gapBottomY <= obs.gapTopY
      ) {
        issues.push({
          level: 'error',
          message: `Gate ${obs.id} gap [${obs.gapTopY}, ${obs.gapBottomY}] must satisfy 0 ≤ top < bottom ≤ ${WAVES_PLAYFIELD_HEIGHT}`,
          beat: obs.beat,
        });
      } else {
        const gapHeight = obs.gapBottomY - obs.gapTopY;
        // 2× playerRadius is the absolute minimum playable gap (any
        // smaller and the player can't fit through even with perfect
        // input). Variant tuning enforces a friendlier floor at
        // build time; this is the engine-level sanity bound.
        if (gapHeight < 2 * chart.playerRadius) {
          issues.push({
            level: 'error',
            message: `Gate ${obs.id} gap height ${gapHeight} is smaller than 2× playerRadius (${2 * chart.playerRadius}) — unhittable`,
            beat: obs.beat,
          });
        }
      }
    } else if (obs.kind === 'saw') {
      if (
        !Number.isFinite(obs.centerY) ||
        obs.centerY < 0 ||
        obs.centerY > WAVES_PLAYFIELD_HEIGHT
      ) {
        issues.push({
          level: 'error',
          message: `Saw ${obs.id} centerY ${obs.centerY} outside playfield`,
          beat: obs.beat,
        });
      }
      if (!Number.isFinite(obs.radius) || obs.radius < 5 || obs.radius > 100) {
        issues.push({
          level: 'error',
          message: `Saw ${obs.id} radius ${obs.radius} out of range (5-100)`,
          beat: obs.beat,
        });
      }
    } else {
      // Discriminated union exhaustiveness — unreachable in compiled
      // code, but surfaces hand-edited JSON with a bad `kind`.
      const bad = obs as { kind?: unknown };
      issues.push({
        level: 'error',
        message: `Obstacle has unknown kind: ${String(bad.kind)}`,
      });
    }
  }

  validateSameBeatFeasibility(chart, sorted, issues);
}

interface SafeBand {
  lo: number;
  hi: number;
}

function obstacleSafeBands(obs: WavesObstacle, playerRadius: number): SafeBand[] {
  if (obs.kind === 'gate') {
    return [{ lo: obs.gapTopY + playerRadius, hi: obs.gapBottomY - playerRadius }];
  }
  // Saw: the safe band is the playfield minus the swept circle of
  // radius (saw.radius + playerRadius) centred on saw.centerY.
  const dangerLo = obs.centerY - obs.radius - playerRadius;
  const dangerHi = obs.centerY + obs.radius + playerRadius;
  const bands: SafeBand[] = [];
  if (dangerLo > 0) {
    bands.push({ lo: 0, hi: dangerLo });
  }
  if (dangerHi < WAVES_PLAYFIELD_HEIGHT) {
    bands.push({ lo: dangerHi, hi: WAVES_PLAYFIELD_HEIGHT });
  }
  return bands;
}

function intersectBands(a: SafeBand[], b: SafeBand[]): SafeBand[] {
  const out: SafeBand[] = [];
  for (const ba of a) {
    for (const bb of b) {
      const lo = Math.max(ba.lo, bb.lo);
      const hi = Math.min(ba.hi, bb.hi);
      if (lo < hi) {
        out.push({ lo, hi });
      }
    }
  }
  return out;
}

/**
 * Two obstacles at exactly the same beat are evaluated by the engine
 * against a single playerY sample. If their feasible safe bands don't
 * overlap (e.g. a floor-spike gate plus a ceiling-spike gate, or a
 * gate whose gap sits inside a saw's collision swept area), the chart
 * is literally unclearable — the player dies at that beat regardless
 * of any input pattern. Surface this as a hard validation error so a
 * malformed user upload never ships an impossible run.
 */
function validateSameBeatFeasibility(
  chart: WavesChart,
  sortedObstacles: ReadonlyArray<WavesObstacle>,
  issues: ChartValidationIssue[],
): void {
  let groupStart = 0;
  while (groupStart < sortedObstacles.length) {
    let groupEnd = groupStart + 1;
    while (
      groupEnd < sortedObstacles.length &&
      sortedObstacles[groupEnd].beat === sortedObstacles[groupStart].beat
    ) {
      groupEnd += 1;
    }
    if (groupEnd - groupStart >= 2) {
      let feasible: SafeBand[] = obstacleSafeBands(
        sortedObstacles[groupStart],
        chart.playerRadius,
      );
      for (let i = groupStart + 1; i < groupEnd && feasible.length > 0; i += 1) {
        feasible = intersectBands(
          feasible,
          obstacleSafeBands(sortedObstacles[i], chart.playerRadius),
        );
      }
      if (feasible.length === 0) {
        const ids = sortedObstacles.slice(groupStart, groupEnd).map((o) => o.id).join(', ');
        issues.push({
          level: 'error',
          message: `Obstacles [${ids}] at beat ${sortedObstacles[groupStart].beat} have no overlapping safe band — unclearable`,
          beat: sortedObstacles[groupStart].beat,
        });
      }
    }
    groupStart = groupEnd;
  }
}
